import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from "chart.js";
import { Line } from "react-chartjs-2";
import { getFusionResultLabel } from "lib/utils/fusionResult";
import { buildTimeseriesComprehensiveOpinion } from "pages/files/image/report/timeseries/utils/buildTimeseriesComprehensiveOpinion";
import { buildSingleMetricChartData, buildChartOptions, toTimeseriesNumeric } from "pages/files/image/report/timeseries/utils/timeseriesChart";
import { getHistoryRelation } from "pages/files/image/history/fileHistoryUtils";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

function pointDifference(file) {
  const values = (Array.isArray(file.click_points) ? file.click_points : [])
    .map((point) => toTimeseriesNumeric(point?.value)).filter((value) => value != null);
  return values.length >= 2 ? Math.max(...values) - Math.min(...values) : null;
}

const displayMetric = (value, unit) => {
  const number = toTimeseriesNumeric(value);
  return number == null ? "-" : `${number.toFixed(1)} ${unit}`;
};

function HistoryImagePreview({ file, onClose, t }) {
  const dialogRef = useRef(null);
  const titleId = useId();
  const [failed, setFailed] = useState(false);
  const title = file.file_name || file.facility_name || t("facilityQr.title");
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog.showModal();
    return () => dialog.close();
  }, []);
  return createPortal(
    <dialog className="facilityQrImagePreview" ref={dialogRef} aria-labelledby={titleId}
      onCancel={event => { event.preventDefault(); event.stopPropagation(); onClose(); }}
      onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="facilityQrImagePreview__content">
        <header><h2 id={titleId}>{title}</h2>
          <button type="button" onClick={onClose} aria-label={t("facilityQr.close")}><X size={20} aria-hidden="true" /></button>
        </header>
        <div className="facilityQrImagePreview__media">
          {failed ? <p role="status">{t("facilityQr.history.imageFailed", { defaultValue: "이미지를 불러오지 못했습니다." })}</p>
            : <img src={file.image_visual} alt={title} onError={() => setFailed(true)} />}
        </div>
      </div>
    </dialog>, document.body,
  );
}

export default function FacilityQrHistory({ files = [], currentFile, onOpenFile, showAnalysis = true, compact = false, variant = "qr" }) {
  const { t, i18n } = useTranslation();
  const [previewFile, setPreviewFile] = useState(null);
  const [comparisonId, setComparisonId] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const latest = files[files.length - 1];
  const comparisonFiles = currentFile ? files.filter((item) => item.upload_id !== currentFile.upload_id) : [];
  const comparison = comparisonFiles.find((item) => item.upload_id === comparisonId) ?? comparisonFiles[comparisonFiles.length - 1];
  const metrics = [
    { label: t("imageFileIndex.timeseries.chart.labels.averageUltrasonic", { defaultValue: "평균 초음파" }), unit: "dB", getValue: (file) => toTimeseriesNumeric(file.avg_decibel), color: "#2563eb" },
    { label: t("imageFileIndex.timeseries.opinion.comprehensive.labels.detectedTemperature", { defaultValue: "검출온도" }), unit: "℃", getValue: (file) => toTimeseriesNumeric(file.thermal_center), color: "#d97706" },
    { label: t("imageFileIndex.timeseries.chart.labels.threePhaseDifference", { defaultValue: "3상 비교 온도차" }), unit: "℃", getValue: pointDifference, color: "#0f766e" },
  ].map(metric => ({ ...metric, label: metric.label.replace(/\s*\((?:dB|°C|℃)\)\s*$/i, "") }));
  const dateLabel = (file) => String(file.shooting_date || file.uploaded_at || "-").replace("T", " ").slice(0, 19);

  return (
    <section className={`facilityQrHistory${variant === "file" ? " facilityQrHistory--file" : ""}`}>
      {!files.length && <p role="status">{t("facilityQr.history.empty", { defaultValue: "조회 가능한 이력이 없습니다." })}</p>}
      {latest && <>
        {showAnalysis && <>
        {!compact && currentFile && comparison && <div className="fileHistoryComparison">
          <h2>{t("fileHistory.compare")}</h2>
          <select aria-label={t("fileHistory.compare")} value={comparison.upload_id} onChange={(event) => setComparisonId(event.target.value)}>
            {comparisonFiles.map((item) => <option key={item.upload_id} value={item.upload_id}>
              {t(`fileHistory.${getHistoryRelation(item, currentFile)}`)} · {dateLabel(item)} · {item.file_name || "-"}
            </option>)}
          </select>
          <div className="fileHistoryTableWrap"><table>
            <thead><tr><th>{t("fileHistory.metric")}</th><th>{t("fileHistory.current")}</th><th>{t("fileHistory.compared")}</th><th>{t("fileHistory.difference")}</th></tr></thead>
            <tbody>{metrics.map((metric) => {
              const before = metric.getValue(currentFile), after = metric.getValue(comparison);
              return <tr key={metric.label}><th>{metric.label}</th><td>{displayMetric(before, metric.unit)}</td><td>{displayMetric(after, metric.unit)}</td>
                <td>{before == null || after == null ? "-" : `${after - before > 0 ? "+" : ""}${displayMetric(after - before, metric.unit)}`}</td></tr>;
            })}</tbody>
          </table></div>
        </div>}
        {!compact && <details className="facilityQrHistorySummary" open={variant === "file" ? undefined : true}><summary hidden={variant !== "file"}>최근 측정 요약 · 종합의견</summary><h2>{t("facilityQr.history.latest", { defaultValue: "최근 측정 상태" })}</h2>
        <p>{dateLabel(latest)} · {getFusionResultLabel(latest.fusion_result_code, i18n, { fallback: "-" })}</p>
        <div className="facilityQrHistoryMetrics">
          {metrics.map((metric) => <div key={metric.label}><span>{metric.label}</span><strong>{displayMetric(metric.getValue(latest), metric.unit)}</strong></div>)}
        </div>
        <h2>{t("facilityQr.history.opinion", { defaultValue: "종합의견" })}</h2>
        <p className="facilityQrHistoryOpinion">{buildTimeseriesComprehensiveOpinion({ files, t })}</p></details>}
        <div className="facilityQrHistoryCharts">
        {(compact ? metrics.slice(1, 2) : metrics).map((metric) => <div className="facilityQrHistoryChart" key={metric.label}>
          <h3>{metric.label} ({metric.unit})</h3>
          {files.some((file) => metric.getValue(file) != null)
            ? <div className="facilityQrHistoryCanvas"><Line
                aria-label={`${metric.label} (${metric.unit})`}
                data={buildSingleMetricChartData(files, { ...metric, borderColor: metric.color, backgroundColor: metric.color })}
                options={buildChartOptions()} /></div>
            : <p>{t("facilityQr.history.noMeasurements", { defaultValue: "측정 데이터가 없습니다." })}</p>}
        </div>)}</div>
        </>}
        {!compact && <><h2>{t("facilityQr.history.list", { defaultValue: "측정 이력 · 오래된순" })} ({files.length})</h2>
        <ol className="facilityQrHistoryList">
          {files.slice(0, visibleCount).map((file) => <li key={file.upload_id} aria-current={currentFile?.upload_id === file.upload_id ? "true" : undefined}>
            {file.image_visual && <button type="button" className="facilityQrHistoryThumbnail"
              aria-label={t("facilityQr.history.enlargeImage", { defaultValue: "{{name}} 이미지 크게 보기", name: file.file_name || file.facility_name || dateLabel(file) })}
              onClick={() => setPreviewFile(file)}>
              <img src={file.image_visual} alt="" loading="lazy"
                onError={event => { event.currentTarget.parentElement.hidden = true; }} />
            </button>}
            <div><strong>{dateLabel(file)}</strong>
              {currentFile && <span className="fileHistoryBadge">{t(`fileHistory.${getHistoryRelation(file, currentFile)}`)}</span>}
              <p>{file.file_name || file.facility_name || "-"} · {getFusionResultLabel(file.fusion_result_code, i18n, { fallback: "-" })}</p>
              <p>{metrics.map((metric) => `${metric.label}: ${displayMetric(metric.getValue(file), metric.unit)}`).join(" · ")}</p>
              {onOpenFile && file.upload_id !== currentFile?.upload_id && <button type="button" className="btn-bg-blue" onClick={() => onOpenFile(file)}>{t("fileHistory.openFile")}</button>}
            </div>
          </li>)}
        </ol>{files.length > visibleCount && <button type="button" onClick={() => setVisibleCount(count => count + 50)}>이력 더 보기 ({visibleCount} / {files.length})</button>}</>}
      </>}
      {previewFile && <HistoryImagePreview file={previewFile} t={t} onClose={() => setPreviewFile(null)} />}
    </section>
  );
}
